import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import PageHelpLink from "../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../constants/helpRouteMap";
import {
  apiErrorMessage,
  CaptureForm,
  FormEmbedPayload,
  FormStats,
  FormStatsBySource,
  FormStatsSeriesPoint,
  getForm,
  getFormEmbed,
  getFormStats,
} from "../../../helpers/forms_helper";

/**
 * Snippet para colar no site do cliente (DOC 03 § 4.13).
 */
const FormEmbed = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const formId = Number(id);

  document.title = t("forms.documentTitleEmbed");

  const helpHref = helpUrlForRoute("/forms", i18n.language || "pt-BR");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<CaptureForm | null>(null);
  const [embed, setEmbed] = useState<FormEmbedPayload | null>(null);
  const [stats, setStats] = useState<FormStats | null>(null);
  const [previewKey, setPreviewKey] = useState(0);

  const load = useCallback(async () => {
    if (!formId || Number.isNaN(formId)) {
      setError(t("forms.error.load"));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [formRes, embedRes, statsRes] = await Promise.all([
        getForm(formId),
        getFormEmbed(formId),
        getFormStats(formId, { days: 7 }),
      ]);
      setForm(formRes.data);
      setEmbed(embedRes.data);
      setStats(statsRes.data);
    } catch (e) {
      setError(apiErrorMessage(e, t("forms.error.load")));
    } finally {
      setLoading(false);
    }
  }, [formId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const copySnippet = async () => {
    if (!embed?.snippet) return;
    try {
      await navigator.clipboard.writeText(embed.snippet);
      toast.success(t("forms.embedCopied"));
    } catch {
      toast.error(t("forms.error.copy"));
    }
  };

  const copyLandingLink = async () => {
    if (!embed?.landing_url) return;
    try {
      await navigator.clipboard.writeText(embed.landing_url);
      toast.success(t("forms.embed.landingCopied"));
    } catch {
      toast.error(t("forms.error.copy"));
    }
  };

  const statsEmpty = useMemo(() => {
    if (!stats) return false;
    return (
      stats.submissions === 0 &&
      stats.confirmed === 0 &&
      stats.accepted === 0 &&
      stats.pending_doi === 0
    );
  }, [stats]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("forms.embedTitle", { name: form?.name || "" })}
          pageTitle={t("menu.automation")}
          actions={
            helpHref ? (
              <PageHelpLink href={helpHref} label={t("forms.help")} />
            ) : undefined
          }
        />

        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <Row>
            <Col lg={8}>
              <Card>
                <CardBody>
                  <p className="text-muted">{t("forms.embedIntro")}</p>

                  <LabelBlock label={t("forms.embed.publicUrl")}>
                    <code className="user-select-all">{embed?.public_url}</code>
                  </LabelBlock>

                  <LabelBlock label={t("forms.embed.landingUrl")}>
                    <code className="user-select-all">{embed?.landing_url}</code>
                  </LabelBlock>

                  <LabelBlock label={t("forms.embed.snippet")}>
                    <pre
                      className="bg-light border rounded p-3 mb-0"
                      style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}
                    >
                      <code>{embed?.snippet}</code>
                    </pre>
                  </LabelBlock>

                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <Button color="primary" onClick={() => void copySnippet()}>
                      {t("forms.embed.copy")}
                    </Button>
                    {embed?.landing_url && (
                      <Button
                        color="soft-primary"
                        onClick={() => void copyLandingLink()}
                      >
                        {t("forms.embed.copyLanding")}
                      </Button>
                    )}
                    {embed?.landing_url && (
                      <Button
                        color="soft-success"
                        tag="a"
                        href={embed.landing_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("forms.embed.openLanding")}
                      </Button>
                    )}
                    {embed?.public_url && (
                      <Button
                        color="soft-info"
                        tag="a"
                        href={embed.public_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("forms.embed.openPublic")}
                      </Button>
                    )}
                    <Button
                      color="light"
                      tag={Link}
                      to={`/forms/${formId}`}
                    >
                      {t("forms.edit")}
                    </Button>
                    <Button color="light" tag={Link} to="/forms">
                      {t("forms.backToList")}
                    </Button>
                  </div>

                  <Alert color="info" className="mt-4 mb-0">
                    {t("forms.embed.note")}
                  </Alert>
                </CardBody>
              </Card>

              {embed?.landing_url && (
                <Card className="mt-3">
                  <CardHeader className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
                    <h5 className="card-title mb-0">
                      {t("forms.embed.livePreviewTitle")}
                    </h5>
                    <Button
                      color="light"
                      size="sm"
                      type="button"
                      onClick={() => setPreviewKey((k) => k + 1)}
                    >
                      {t("forms.embed.refreshPreview")}
                    </Button>
                  </CardHeader>
                  <CardBody>
                    <p className="text-muted small">
                      {t("forms.embed.livePreviewHint")}
                    </p>
                    <iframe
                      key={previewKey}
                      src={embed.landing_url}
                      title={t("forms.embed.livePreviewFrameTitle")}
                      sandbox="allow-scripts allow-forms allow-same-origin"
                      style={{
                        width: "100%",
                        height: 420,
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        background: "#fff",
                      }}
                    />
                  </CardBody>
                </Card>
              )}
            </Col>
            <Col lg={4}>
              <Card>
                <CardBody>
                  <h5 className="mb-3">{t("forms.stats.title")}</h5>
                  {statsEmpty && embed?.landing_url && (
                    <Alert color="warning" className="small py-2">
                      <p className="mb-2">{t("forms.stats.emptyCta")}</p>
                      <div className="d-flex flex-wrap gap-2">
                        <Button
                          color="warning"
                          size="sm"
                          outline
                          tag="a"
                          href={embed.landing_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t("forms.embed.openLanding")}
                        </Button>
                        <Button
                          color="warning"
                          size="sm"
                          onClick={() => void copyLandingLink()}
                        >
                          {t("forms.embed.copyLanding")}
                        </Button>
                      </div>
                    </Alert>
                  )}
                  {stats ? (
                    <>
                      <ul className="list-unstyled mb-0 small">
                        <li className="d-flex justify-content-between py-1 border-bottom">
                          <span className="text-muted">
                            {t("forms.stats.submissions")}
                          </span>
                          <strong>{stats.submissions}</strong>
                        </li>
                        <li className="d-flex justify-content-between py-1 border-bottom">
                          <span className="text-muted">
                            {t("forms.stats.confirmed")}
                          </span>
                          <strong>{stats.confirmed}</strong>
                        </li>
                        <li className="d-flex justify-content-between py-1">
                          <span className="text-muted">
                            {t("forms.stats.conversion")}
                          </span>
                          <strong>
                            {stats.conversion_pct != null
                              ? `${stats.conversion_pct}%`
                              : "—"}
                          </strong>
                        </li>
                        <SourceBreakdown bySource={stats.by_source} t={t} />
                      </ul>
                      {stats.series && stats.series.points.length > 0 && (
                        <SeriesBySource series={stats.series.points} t={t} />
                      )}
                    </>
                  ) : (
                    <p className="text-muted small mb-0">
                      {t("forms.stats.empty")}
                    </p>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
      <ToastContainer autoClose={3000} />
    </div>
  );
};

function LabelBlock({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-3">
      <div className="fw-medium mb-1">{label}</div>
      {children}
    </div>
  );
}

/** Breakdown por canal — só renderiza se houver contagem conhecida. */
function SourceBreakdown({
  bySource,
  t,
}: {
  bySource?: FormStatsBySource | null;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  if (!bySource) return null;

  const rows = (
    [
      ["embed", bySource.embed],
      ["landing", bySource.landing],
      ["api", bySource.api],
    ] as const
  ).filter(([, count]) => count > 0);

  if (rows.length === 0) return null;

  return (
    <li className="pt-2 mt-1 border-top">
      <div className="text-muted mb-1">{t("forms.stats.bySource")}</div>
      {rows.map(([key, count]) => (
        <div
          key={key}
          className="d-flex justify-content-between py-1 small"
        >
          <span>{t(`forms.stats.source.${key}`)}</span>
          <strong>{count}</strong>
        </div>
      ))}
    </li>
  );
}

/** Série diária (7d) — barras CSS leves, sem lib de gráfico. */
function SeriesBySource({
  series,
  t,
}: {
  series: FormStatsSeriesPoint[];
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const sources = ["embed", "landing", "api", "legacy"] as const;
  const maxTotal = Math.max(
    1,
    ...series.map((p) => p.embed + p.landing + p.api + p.legacy)
  );

  const formatDay = (iso: string) => {
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric" });
  };

  return (
    <div className="mt-3 pt-3 border-top">
      <div className="text-muted small mb-2">{t("forms.stats.seriesTitle")}</div>
      <div className="d-flex flex-column gap-2">
        {series.map((point) => {
          const total = point.embed + point.landing + point.api + point.legacy;
          return (
            <div key={point.date} className="small">
              <div className="d-flex justify-content-between text-muted mb-1">
                <span>{formatDay(point.date)}</span>
                <span>{total}</span>
              </div>
              {total === 0 ? (
                <div className="bg-light rounded" style={{ height: 6 }} />
              ) : (
                <div className="d-flex rounded overflow-hidden" style={{ height: 8 }}>
                  {sources.map((key) => {
                    const count = point[key];
                    if (count <= 0) return null;
                    const pct = (count / maxTotal) * 100;
                    const colors: Record<typeof key, string> = {
                      embed: "#405189",
                      landing: "#0ab39c",
                      api: "#299cdb",
                      legacy: "#adb5bd",
                    };
                    return (
                      <div
                        key={key}
                        title={`${t(`forms.stats.source.${key}`)}: ${count}`}
                        style={{
                          width: `${pct}%`,
                          backgroundColor: colors[key],
                          minWidth: count > 0 ? 4 : 0,
                        }}
                      />
                    );
                  })}
                </div>
              )}
              {total > 0 && (
                <div className="d-flex flex-wrap gap-2 mt-1 text-muted">
                  {sources
                    .filter((key) => point[key] > 0)
                    .map((key) => (
                      <span key={key}>
                        {t(`forms.stats.source.${key}`)}: {point[key]}
                      </span>
                    ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default FormEmbed;
